/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useClientHistory } from "../../hooks/useClientHistory";
import { DataState } from "@/shared/ui/components/basic/data-state/DataState";
import { SectionCard } from "@/features/property-management/components/PropertyDetailView/SectionCard";
import { ConfirmDialog } from "@/features/property-management/components/publications-center/ConfirmDialog";
import {
  AddInteractionForm,
  InteractionEventCard,
  InteractionsEmpty,
  InteractionsTypeFilter,
  matchesInteractionsFilter,
  type InteractionsFilter,
} from "./client-interactions-ui";
import { cn } from "@/shared/ui/utils/tailwind";
import { PlusIcon, Text, UnstyledButton } from '@/shared/ui';

interface Props {
  clientId: string;
  agencyId: string;
}

/**
 * Interactions tab: add new interactions and browse contact/activity history.
 *
 * Built on the same chrome as the visits, properties-of-interest, documents
 * and proposals tabs beside it — one {@link SectionCard}, the record action as
 * a dashed call-to-action in the header, and one row card per entry.
 */
export function ClientHistoryTab({ clientId, agencyId }: Props) {
  const { t } = useTranslation("features/clients");
  const [filter, setFilter] = useState<InteractionsFilter>("all");
  const [showForm, setShowForm] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const {
    events,
    isLoading,
    isError,
    hasMore,
    loadMore,
    logInteraction,
    deleteInteraction,
  } = useClientHistory({ agencyId, clientId });

  const handleAddInteraction = async (channel: string, comment: string) => {
    await logInteraction({ channel, comment, kind: "interaction" });
    setShowForm(false);
  };

  const eventLabels: Record<string, string> = {
    visit: t("profile.history.types.visit"),
    negotiation: t("profile.history.types.negotiation"),
    interest: t("profile.history.types.interest"),
    interaction: t("profile.history.types.interaction"),
  };

  const filtered = useMemo(
    () => events.filter((event) => matchesInteractionsFilter(event, filter)),
    [events, filter],
  );

  const confirmDelete = async () => {
    if (!pendingDeleteId) return;
    const id = pendingDeleteId;
    setPendingDeleteId(null);
    await deleteInteraction(id);
  };

  return (
    <SectionCard
      data-testid="client-interactions"
      title={t("profile.tabs.history", "Interactions")}
      titleAccessory={(
        <RecordInteractionButton
          label={t("profile.history.form.newInteraction", "Registrar interação")}
          onClick={() => setShowForm(true)}
        />
      )}
    >
      <AddInteractionForm
        open={showForm}
        onClose={() => setShowForm(false)}
        onSubmit={handleAddInteraction}
        labels={{
          title: t("profile.history.form.newInteraction", "Registrar interação"),
          channel: t("profile.history.form.channel", "Canal"),
          comment: t("profile.history.form.comment", "Descrição"),
          placeholder: t("profile.history.form.placeholder"),
          add: t("profile.history.form.add"),
          channels: {
            whatsapp: t("profile.history.form.channels.whatsapp"),
            email: t("profile.history.form.channels.email"),
            phone: t("profile.history.form.channels.phone"),
          },
        }}
      />
      <InteractionsTypeFilter
        value={filter}
        onChange={setFilter}
        labels={{
          all: t("profile.history.filters.all", "All"),
          interaction: t("profile.history.filters.interactions", "Interactions"),
          visit: t("profile.history.filters.visits", "Visits"),
        }}
      />
      <DataState
        isLoading={isLoading}
        isError={isError}
        isEmpty={filtered.length === 0}
        loadingFallback={<InteractionsSkeleton />}
        emptyState={<InteractionsEmpty label={t("profile.history.empty")} />}
      >
        <div className="flex flex-col gap-[var(--space-3)]">
          {filtered.map((event, idx) => (
            <InteractionEventCard
              key={`${event.entity_id}-${idx}`}
              event={event}
              labels={eventLabels}
              t={t}
              onDelete={setPendingDeleteId}
            />
          ))}
          {hasMore && filter === "all" ? (
            <UnstyledButton
              type="button"
              onClick={loadMore}
              className={cn(
                "mt-[var(--space-1)] inline-flex h-9 items-center justify-center self-center px-[var(--space-4)]",
                "rounded-(--radius-md) border border-[var(--border-subtle)]",
                "bg-[var(--surface-primary)] text-[length:var(--text-meta)] [font-weight:var(--font-medium)]",
                "text-[var(--text-secondary)] hover:bg-[var(--surface-hover)] hover:text-[var(--text-primary)]",
                "cursor-pointer transition-all duration-(--duration-fast)",
              )}
            >
              {t("profile.history.loadMore")}
            </UnstyledButton>
          ) : null}
        </div>
      </DataState>
      {pendingDeleteId ? (
        <ConfirmDialog
          message={t(
            "profile.history.deleteConfirm",
            "Remove this interaction?",
          )}
          confirmLabel={t("profile.history.delete", "Delete")}
          cancelLabel={t("profile.history.cancel", "Cancel")}
          onConfirm={confirmDelete}
          onCancel={() => setPendingDeleteId(null)}
        />
      ) : null}
    </SectionCard>
  );
}

/**
 * Dashed call-to-action that reveals the add-interaction form. Mirrors the
 * schedule-visit, add-property, upload-document and record-proposal CTAs.
 *
 * @param props - The button label and click handler.
 * @returns The rendered record-interaction button.
 */
function RecordInteractionButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <UnstyledButton
      type="button"
      data-testid="client-record-interaction"
      onClick={onClick}
      aria-label={label}
      className="flex items-center gap-(--space-1-5) rounded-(--radius-md) border border-dashed border-[var(--border-subtle)] px-(--space-3) py-(--space-1-5) text-[var(--seed-base)] cursor-pointer bg-transparent hover:bg-[var(--surface-hover)]"
    >
      <PlusIcon width={13} height={13} />
      <Text as="span" className="text-[length:var(--text-meta)] [font-weight:var(--font-semibold)]">{label}</Text>
    </UnstyledButton>
  );
}

/**
 * Row-shaped loading placeholder, sized to the interaction rows it stands in
 * for so the card does not resize when the history arrives.
 *
 * @returns The rendered skeleton list.
 */
function InteractionsSkeleton() {
  return (
    <div className="flex flex-col gap-[var(--space-3)]">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="h-[52px] rounded-(--radius-md) bg-[var(--surface-tertiary)] animate-pulse" />
      ))}
    </div>
  );
}
